import { useState } from "react";

import { Formik, Form } from "formik";
import { Typography, Button, Pane, Toastr } from "neetoui";
import { Input, MultiEmailInput } from "neetoui/formik";
import PropTypes from "prop-types";
import { pluck, any, assoc } from "ramda";
import { useTranslation } from "react-i18next";

import CodeBlock from "./CodeBlock";
import { EMAIL_TYPES, FRAMEWORK_SELECT_OPTIONS } from "./constants";
import { useEmailEmbedCode } from "./mutations";
import {
  getEmailWidgetSnippetFormInitialValues,
  getEmailWidgetSnippetValidationSchema,
  getSampleUserIdentity,
} from "./utils";

const CodeSnippet = ({
  isPaneOpen,
  onClose,
  codeString,
  subject = "",
  body = "",
  emailType = EMAIL_TYPES.embedCode,
  enabledWidgets = {},
}) => {
  const { t } = useTranslation();
  const [selectedFramework, setSelectedFramework] = useState(
    FRAMEWORK_SELECT_OPTIONS[0]
  );
  const emailMutation = useEmailEmbedCode(onClose);

  const codeToDisplay =
    emailType === EMAIL_TYPES.userIdentity
      ? getSampleUserIdentity(enabledWidgets, selectedFramework.value)
      : codeString;

  const handleSubmit = formData => {
    if (any(({ valid }) => !valid, formData.emails)) {
      Toastr.error(t("neetoMolecules.widget.email.fields.emails.invalidError"));

      return;
    }

    emailMutation.mutate({
      subject: formData.subject,
      emails: pluck("value", formData.emails),
      message: body.ref?.current.innerHTML ?? body,
      snippet: codeToDisplay,
    });
  };

  const renderCodeBlockProps = () => {
    let props = {
      codeString: codeToDisplay,
      showCopyButton: false,
      className: "neeto-ui-rounded-lg",
    };
    if (emailType === EMAIL_TYPES.userIdentity) {
      props = assoc(
        "frameworkProps",
        { selectedFramework, setSelectedFramework },
        props
      );
    }

    return props;
  };

  return (
    <Pane {...{ onClose }} isOpen={isPaneOpen}>
      <Formik
        initialValues={getEmailWidgetSnippetFormInitialValues(subject)}
        validationSchema={getEmailWidgetSnippetValidationSchema()}
        onSubmit={handleSubmit}
      >
        <Form>
          <Pane.Header>
            <Typography style="h2">
              {t("neetoMolecules.widget.email.title")}
            </Typography>
          </Pane.Header>
          <Pane.Body>
            <div className="flex w-full flex-col gap-y-4">
              <MultiEmailInput
                required
                label={t("neetoMolecules.widget.email.fields.emails.label")}
                name="emails"
                placeholder={t(
                  "neetoMolecules.widget.email.fields.emails.placeholder"
                )}
              />
              <Input
                required
                label={t("neetoMolecules.widget.email.fields.subject.label")}
                name="subject"
                type="text"
              />
              <Typography style="body2">{body}</Typography>
              <CodeBlock {...renderCodeBlockProps()} />
            </div>
          </Pane.Body>
          <Pane.Footer className="gap-x-2">
            <Button
              disabled={emailMutation.isPending}
              label={t("neetoMolecules.widget.email.actions.send")}
              loading={emailMutation.isPending}
              type="submit"
            />
            <Button
              label={t("neetoMolecules.widget.email.actions.cancel")}
              style="text"
              onClick={onClose}
            />
          </Pane.Footer>
        </Form>
      </Formik>
    </Pane>
  );
};

CodeSnippet.propTypes = {
  isPaneOpen: PropTypes.bool,
  onClose: PropTypes.func,
  codeString: PropTypes.string,
  subject: PropTypes.string,
  body: PropTypes.string,
};

export default CodeSnippet;
